import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { userApi } from "@/lib/api";
import { useSession } from "@/lib/store/session";
import { keys } from "./keys";

/**
 * Polls POST user/synchupdate every 30s; when the server's stamp changes
 * (a new activity was processed, prefs saved elsewhere, ...) the cached
 * activity list, user summary and annual summaries are invalidated.
 */
export function useSyncPoll() {
  const userId = useSession((s) => s.userId);
  const qc = useQueryClient();
  const last = useRef<number | null>(null);
  const q = useQuery({
    queryKey: keys.synch(userId),
    queryFn: userApi.synchUpdate,
    enabled: !!userId,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  useEffect(() => {
    if (q.data === undefined) return;
    if (last.current !== null && last.current !== q.data) {
      qc.invalidateQueries({ queryKey: keys.activities(userId) });
      qc.invalidateQueries({ queryKey: keys.user(userId) });
      qc.invalidateQueries({ queryKey: keys.annual(userId) });
    }
    last.current = q.data;
  }, [q.data, qc, userId]);
  return q;
}

/** Record a synchUpdated value returned by a mutation so the poll doesn't refetch needlessly. */
export function noteSynch(qc: ReturnType<typeof useQueryClient>, userId: string | null, value: number | undefined) {
  if (typeof value === "number") qc.setQueryData(keys.synch(userId), value);
}
